import React, { useState, useEffect, useRef } from 'react';
import BasicModal from '../../common/modal';
import PropTypes from 'prop-types';
import { Box, Typography, Toolbar, Container, Autocomplete, TextField, InputAdornment, CircularProgress } from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import debounce from '../../../utils/helpers/debounce';
import SearchNoData from './search-no-data';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from '../../../constants/route-constants';
import PageLoader from '../../common/page-loader/index';
import { APP_KEYS, AUCTION_TYPE, NUMBER, PAGE_SIZE, SEARCH_TYPES } from '../../../constants/app-constants';
import { isValidArray } from '../../../utils/is-array';
import { useSelector } from 'react-redux';
import { prepareGADataAndSendEvent } from '../../../tracking';

const SearchAuctionModal = ({ user, type, eligibleForBid, recentSearch, handleClose, getAuctionsListConnect, setSearchFlagConnect, setRecentSearchConnect, getAutoSuggestionsConnect }) => {
    const { isSearchResults } = useSelector(state => state?.auctions);
    const [searchValue, setSearchValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showRecent, setShowRecent] = useState(true);
    const [showPopup, setShowPopup] = useState(true);
    const [loading, setLoading] = useState(false);
    const ref = useRef({ searchText: '' });
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const navigate = useNavigate();

    const fetchSearchedAuctions = (value) => {
        if (!value || typeof value !== 'object' || !eligibleForBid) return;
        const payload = { page: 0, size: PAGE_SIZE, search: value?.label, type: APP_KEYS.SEARCH, section: type || AUCTION_TYPE.LIVE };
        setLoading(true);
        setShowPopup(false);
        getAuctionsListConnect(payload).then(() => {
            handleClose();
            // Don't add searched data to recent if it already exists
            if (!recentSearch?.find(v => v?.label?.toLowerCase() === value?.label?.toLowerCase())) {
                setRecentSearchConnect({ ...value, tag: SEARCH_TYPES.RECENT_SEARCH });
            }
            setSearchFlagConnect(value?.label);
            navigate(ROUTE_PATH.EXPLORE);
        }).finally(() => setLoading(false));
    };

    useEffect(() => {
        if (isSearchResults && !searchValue) {
            setSearchValue({ label: isSearchResults });
            ref.current.searchText = isSearchResults;
            fetchSuggestions(isSearchResults);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSearchResults]);

    const handleCloseClick = () => {
        if (ref.current.searchText && !searchValue) {
            setSearchFlagConnect(false);
            if (eligibleForBid) {
                const payload = { page: 0, size: PAGE_SIZE, section: type || AUCTION_TYPE.LIVE };
                getAuctionsListConnect(payload);
            }
        }
        handleClose();
    };

    const fetchSuggestions = (value) => {
        setLoadingSuggestions(true);
        getAutoSuggestionsConnect(value).then((res) => {
            setShowRecent(false);
            setSuggestions(res?.suggestions?.map(v => ({ ...v, label: v?.suggestion, tag: SEARCH_TYPES.SUGGESTED_SEARCH })));
        }).catch(() => {
            setSuggestions([]);
        }).finally(() => setLoadingSuggestions(false));
    };

    const handleInputChange = debounce((event) => {
        const { value } = event.target;
        prepareGADataAndSendEvent('searchEnter', user, null, 'Search page', { eventLabel: value });
        setShowPopup(true);
        setShowRecent(false);
        setSearchValue({ label: value });
        fetchSuggestions(value);
    }, 300);

    const handleSelect = (event, val) => {
        event.preventDefault();
        fetchSearchedAuctions(val);
    };

    const handleEnter = (event) => {
        // On ENTER key press fetch searched auctions
        if (event?.keyCode === NUMBER.THIRTEEN && event?.key === APP_KEYS.ENTER) {
            const { value } = event.target;
            const searchObj = { label: value };
            fetchSearchedAuctions(searchObj);
        }
    };

    const handleSearch = () => {
        if (searchValue) fetchSearchedAuctions(searchValue);
    };

    const handleClear = () => {
        setSearchValue('');
        setSuggestions([]);
        setShowRecent(true);
        setShowPopup(false);
    };
    const suggestionsData = (showRecent ? recentSearch : suggestions);
    return (
        <BasicModal open={true} handleClose={handleCloseClick}>
            {loading && <PageLoader />}
            <Box
                sx={{
                    width: '100vw',
                    height: '100%',
                    position: 'fixed',
                    top: 0,
                    left: 0
                }}
                className='bg-white'
            >
                <Toolbar variant="dense" className='bg-primary' sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <ArrowBackOutlinedIcon className='text-white h-8 w-8' onClick={handleCloseClick} />
                    <Typography variant='h6' className='w-full text-center text-white'>SEARCH</Typography>
                </Toolbar>
                <Container maxWidth="sm" className='h-full my-6'>
                    <Autocomplete classes={{ root: 'search-auto-complete' }} size='small'
                        id="combo-box-demo" freeSolo loading={!loadingSuggestions && !isValidArray(suggestions)} loadingText={<SearchNoData />}
                        options={suggestionsData}
                        open={showPopup}
                        groupBy={(option) => option.tag}
                        getOptionLabel={(option) => (option?.label || '')}
                        value={searchValue}
                        sx={{ border: 'none' }}
                        filterOptions={(o) => { return o; }}
                        className='w-full'
                        onChange={handleSelect}
                        renderInput={(params) => <TextField autoFocus={true} {...params} id='searchInput' onKeyDown={handleEnter} value={searchValue} onChange={handleInputChange}
                            InputProps={{
                                ...params?.InputProps,
                                sx: { paddingRight: '9px!important' },
                                startAdornment: (
                                    <InputAdornment onClick={handleSearch} position="start">
                                        <SearchOutlinedIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchValue && (
                                    <InputAdornment position="end" onClick={handleClear}>
                                        {loadingSuggestions ? <CircularProgress color="secondary" className='text-border' sx={{ height: '24px !important', width: '24px!important' }} /> : <CloseOutlinedIcon />}
                                    </InputAdornment>
                                ),
                            }}
                            placeholder='Search make, model or app id' />}
                    />
                </Container>
            </Box>
        </BasicModal>
    );
};
SearchAuctionModal.propTypes = {
    user: PropTypes.object,
    type: PropTypes.string,
    eligibleForBid: PropTypes.bool,
    recentSearch: PropTypes.array,
    handleClose: PropTypes.func,
    getAuctionsListConnect: PropTypes.func,
    setSearchFlagConnect: PropTypes.func,
    setRecentSearchConnect: PropTypes.func,
    getAutoSuggestionsConnect: PropTypes.func
};
export default SearchAuctionModal;
